import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, Method } from "axios";
import { ServerAPIResponse, GetLoggedOnUserResponseDto, UserProfile } from "./InterfacesServer";
import { GlobalRole, RoutePathNames } from "./Constants";
import { ReactElement } from "react";
import withReactContent, { ReactSweetAlertOptions } from "sweetalert2-react-content";
import Swal, { SweetAlertResult, SweetAlertInput } from "sweetalert2";
import { store } from "../store/store";
import { setLoggedOnUserProfile } from "../store/loggedOnUserProfile";
import { infoColor, secondaryColor } from "../layouts/theme";
import { setAppConfig } from "../store/appConfig";
import { setServerAppConfig } from "../store/ServerAppConfig";
import { AppConfig, MainMenuItem } from "./InterfacesClient";
import { getAllVendors, getAllVendorUsers, getVendorInvitationsReport } from "../api-services/VendorOnboardingApi";
import { setVendors } from "../store/vendors";
import { setVendorUsers } from "../store/vendorUsers";
import { setVendorUsersDict } from "../store/vendorUsersDict";
import { setVendorInvitationsReportDict } from "../store/vendorInvitationsReportDict";
import { setVendorsDict } from "../store/vendorsDict";


export async function loadAppConfigFromSettingsFile() {
    const dispatch = store.dispatch;
    await fetch("appsettings.json", { cache: 'reload' }).then<AppConfig>(r => r.json())
        .then((data) => {
            const appConfig: AppConfig = {
                SERVER_URL: data.SERVER_URL ?? process.env.REACT_APP_SERVER_URL ?? "",
                ENVIRONMENT: data.ENVIRONMENT ?? process.env.REACT_APP_ENVIRONMENT ?? "",
                APP_VERSION: data.APP_VERSION ?? process.env.REACT_APP_VERSION ?? "",
                APP_LAST_UPDATED: Number(data.APP_LAST_UPDATED ?? process.env.REACT_APP_LAST_UPDATED ?? 0) * 1000,
            }
            dispatch(setAppConfig(appConfig));
        })
        .catch(function (error: AxiosError<ServerAPIResponse<any>>) {
            console.log(error);
        });
}


export async function callServerAPI<Type>(method: Method, url: string, data: any = null, doNotThrowErrorOnFailure: boolean, responseType: import("axios").ResponseType = 'json', absoluteUrl: boolean = false, showAlertError: boolean = true): Promise<AxiosResponse<Type>> {
    return await new Promise(async (resolve, reject) => {
        const appConfig = store.getState().appConfig.value;
        let config: AxiosRequestConfig = {}

        config.method = method; config.url = absoluteUrl ? url : appConfig.SERVER_URL + url; config.withCredentials = true; config.data = data
        if (responseType) config.responseType = responseType
        await axios.request(config)
            .then(function (response: AxiosResponse<Type>) {
                resolve(response)
            })
            .catch(function (error: AxiosError<ServerAPIResponse<any>>) {
                console.log(error);
                if (error?.response?.status === 401) {
                    localStorage.setItem("loginRedirectedFromUrl", window.location.href);
                    let url = new URL(window.location.origin + '/' + RoutePathNames.LOGIN)
                    window.location.replace(url.href);
                }
                else {
                    if (showAlertError) fireAxiosErrorMySwal(error)
                    if (!doNotThrowErrorOnFailure) reject(error)
                }
            });
    });
}

export async function callServerGetLoggedonUser(): Promise<AxiosResponse<ServerAPIResponse<GetLoggedOnUserResponseDto>>> {
    const dispatch = store.dispatch;
    const appConfig = store.getState().appConfig.value;
    return await new Promise(async (resolve, reject) => {
        var config: AxiosRequestConfig = {}
        config.method = 'GET'; config.data = null; config.url = appConfig.SERVER_URL + '/api/get_loggedon_user'; config.withCredentials = true
        await axios.request(config)
            .then(async function (response: AxiosResponse<ServerAPIResponse<GetLoggedOnUserResponseDto>>) {
                await getVendorInvitationsReport().then(async vendorInvitationsReport => {
                    dispatch(setVendorInvitationsReportDict(new Map(vendorInvitationsReport.sort((a, b) => (b.Success === a.Success) ? 0 : b.Success ? 1 : -1).map(vi => [vi.Email, vi]))));
                    await getAllVendors().then(async vendors => {
                        dispatch(setVendors(vendors))
                        dispatch(setVendorsDict(new Map(vendors.map(v => [v.VendorCode, v]))));
                        await getAllVendorUsers().then(vendorUsers => {
                            dispatch(setVendorUsers(vendorUsers));
                            dispatch(setVendorUsersDict(new Map(vendorUsers.map(vu => [vu.Email, vu]))));
                        })
                    });
                });
                dispatch(setLoggedOnUserProfile(response.data.details.UserLoggedOn));
                dispatch(setServerAppConfig(response.data.details.ServerAppConfig));
                resolve(response)
            })
            .catch(function (error: AxiosError<ServerAPIResponse<any>>) {
                console.log(error);
                reject(error)
            });
    });
}

export function callServerTepngUserLogout() {
    const dispatch = store.dispatch;
    const appConfig = store.getState().appConfig.value;
    dispatch(setLoggedOnUserProfile(null));
    let url = new URL(window.location.origin)
    // needed to logout of microsoft - the frond end url needs to be added to Azure App registration Redirect URIs to allow for logout redirect
    window.location.replace(appConfig.SERVER_URL + '/.auth/logout?post_logout_redirect_uri=' + window.encodeURIComponent(url.href));
}


export async function fireAxiosErrorMySwal(error: AxiosError<ServerAPIResponse<any>>) {
    if (error?.response?.data instanceof Blob && error?.response?.data?.type && error?.response?.data?.type.toLowerCase().indexOf('json') !== -1) {
        var errorString = JSON.parse(await error.response.data.text());
        showAlertError(errorString.message)
    }
    else {
        if (error?.response == null)
            showAlertError(error.message)
        else if (error?.response?.data?.message == null)
            showAlertError(error.response.status + ' ' + error.response.statusText)
        else
            showAlertError(error?.response?.data?.message)
    }
}

export function showAlertError(message: string) {
    const MySwal = withReactContent(Swal);
    MySwal.fire(alertError(message))
}

function alertError(message: string): ReactSweetAlertOptions {
    return {
        icon: 'warning',
        title: '',
        html: `<div style="max-height:200px; overflow-y:auto;"><small> ${message}</small></div>`,
        showDenyButton: true,
        denyButtonText: `<small>OK</small>`,
        showConfirmButton: false,
    }
}

export function showAlertInfo(message: string, title: string = ''): Promise<SweetAlertResult<any>> {
    const MySwal = withReactContent(Swal);
    return MySwal.fire({
        title: `<span style="color:${infoColor}; font-size:16px">${title}</span>`,
        html: `<div style="max-height:200px; overflow-y:auto;"><small> ${message.replace(/\n/g, '<br/><br/>')}</small></div>`,
        icon: 'info',
        iconColor: '#999999',
        showDenyButton: true,
        denyButtonText: `<small>OK</small>`,
        denyButtonColor: infoColor,
        showConfirmButton: false,
    })
}

export function showConfirmBox(message: string, title: string = 'Confirm', yesButtonText: string = 'Yes', noButtonText: string = 'No'): Promise<SweetAlertResult<any>> {
    const MySwal = withReactContent(Swal);
    return MySwal.fire({
        title: `<span style="color:${infoColor}; font-size:16px">${title}</span>`,
        html: `<small> ${message}</small>`,
        icon: 'question',
        iconColor: '#999999',
        cancelButtonColor: infoColor,
        cancelButtonText: noButtonText,
        showCancelButton: true,
        confirmButtonColor: secondaryColor,
        confirmButtonText: yesButtonText

    })
}

export function showInputBox(message: string, title: string = 'Confirm', inputPlaceholder: string = '', okButtonText: string = 'OK', cancelButtonText: string = 'Cancel', inputType: SweetAlertInput = 'textarea'): Promise<SweetAlertResult<any>> {
    const MySwal = withReactContent(Swal);
    return MySwal.fire({
        title: `<span style="font-size:16px">${title}</span>`,
        html: `<small style="color:#cc0000"> ${message}</small>`,
        confirmButtonText: okButtonText,
        confirmButtonColor: secondaryColor,
        cancelButtonColor: infoColor,
        cancelButtonText: cancelButtonText,
        showCancelButton: true,
        input: inputType,
        inputPlaceholder: inputPlaceholder,
        allowEnterKey: true,
        allowEscapeKey: true,
        inputAutoTrim: true,
    })
}


export function showBackDrop(message: string) {
    return {
        title: message,
        html: '<div style="height:150px;width:150px;margin: 0 auto;"><img style="width: 100px;" src="../loading-icon-red.gif" /><br> <strong> Please Wait... </strong> </div>', showConfirmButton: false, showCancelButton: false, allowOutsideClick: false,
    }
}



export function checkGlobalMenuEnabled(mainMenuItem: MainMenuItem, userLoggedOn: UserProfile): boolean {
    switch (mainMenuItem.pathName) {
        case RoutePathNames.HOMEPAGE:
            return true;
        case RoutePathNames.VENDORS:
            return userLoggedOn.Roles.includes(GlobalRole.USER);
        case RoutePathNames.VENDOR_USERS:
            return userLoggedOn.Roles.includes(GlobalRole.USER);
        case RoutePathNames.SUPPLEMENTARY_VENDOR_EMAIL_REQUESTS:
            return userLoggedOn.Roles.includes(GlobalRole.SUPPLIER_ONBOARDING_MANAGER);
        default:
            return true
    }
}


/**
 * Returns a descriptive message for a server error
 */
export function getDescriptiveErrorMessage(error: AxiosError<ServerAPIResponse<any>>): ReactElement {
    console.log(error)
    if (error.response === undefined) return <span>{error.toString()}</span>;
    return <span>{error.response.data.message ?? error.response.statusText + ' - ' + error.response.data}</span>
}

/**
 * Returns a plain span tag with text as InnerText
 */
export function generatePlainElementFromText(text: string): ReactElement {
    return <span>{text}</span>
}

export function convertHtmlToText(inputText: string): string {
    var returnText = "" + inputText;

    //-- remove BR tags and replace them with line break
    returnText = returnText.replace(/<br>/gi, "\n");
    returnText = returnText.replace(/<br\s\/>/gi, "\n");
    returnText = returnText.replace(/<br\/>/gi, "\n");

    //-- remove P and A tags but preserve what's inside of them
    returnText = returnText.replace(/<p.*>/gi, "\n");
    returnText = returnText.replace(/<a.*href="(.*?)".*>(.*?)<\/a>/gi, " $2 ($1)");

    //-- remove all inside SCRIPT and STYLE tags
    returnText = returnText.replace(/<script.*>[\w\W]{1,}(.*?)[\w\W]{1,}<\/script>/gi, "");
    returnText = returnText.replace(/<style.*>[\w\W]{1,}(.*?)[\w\W]{1,}<\/style>/gi, "");
    //-- remove all else
    returnText = returnText.replace(/<(?:.|\s)*?>/g, "");

    //-- get rid of more than 2 multiple line breaks:
    returnText = returnText.replace(/(?:(?:\r\n|\r|\n)\s*){2,}/gim, "\n\n");

    //-- get rid of more than 2 spaces:
    returnText = returnText.replace(/ +(?= )/g, '');

    //-- get rid of html-encoded characters:
    returnText = returnText.replace(/&nbsp;/gi, " ");
    returnText = returnText.replace(/&amp;/gi, "&");
    returnText = returnText.replace(/&quot;/gi, '"');
    returnText = returnText.replace(/&lt;/gi, '<');
    returnText = returnText.replace(/&gt;/gi, '>');

    return returnText;
}


export function truncateTextToWhiteSpace(text: string, numChars: number): string {
    if (text.length <= numChars)
        return text
    var truncatedText = text.substring(0, numChars)
    return truncatedText.substring(0, truncatedText.lastIndexOf(' ')) + " ..."
}

export function formatNumberWithCommas(x: number | string, numDecimalPlaces: number): string {
    if (typeof x === 'string') x = parseFloat(x);
    if (isNaN(x)) return '';
    const options = {
        minimumFractionDigits: 0,
        maximumFractionDigits: numDecimalPlaces, // Ensures no more than two decimal places
    };
    return x.toLocaleString('en-US', options);
}

export function formatLongDateString(dtStr: Date, includeTime = false): string {
    var dateOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: '2-digit' };
    var timeOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
    var dt = new Date(dtStr)
    return dt.toLocaleDateString('en-GB', dateOptions) + (includeTime ? " " + dt.toLocaleTimeString('en-GB', timeOptions) : '')
}

export function formatShortDateString(dtStr: Date, includeTime = false): string {
    var dateOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: '2-digit' };
    var timeOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
    var dt = new Date(dtStr)
    return dt.toLocaleDateString('en-GB', dateOptions) + (includeTime ? " " + dt.toLocaleTimeString('en-GB', timeOptions) : '')
}

export function formatTimeString(dtStr: Date): string {
    var timeOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
    var dt = new Date(dtStr)
    return dt.toLocaleTimeString('en-GB', timeOptions)
}

export function getCurrentTimeString() {
    const date = new Date()
    return ('0' + (date.getUTCHours())).slice(-2) + ':' + ('0' + (date.getUTCMinutes())).slice(-2) + ':' + ('0' + (date.getUTCSeconds())).slice(-2) + ':' + date.getUTCMilliseconds()
}

export function formatDateyyyymmdd(date: Date | null | undefined) {
    if (date == null) return ""
    date = new Date(date);
    const day = `${date.getDate() < 10 ? '0' : ''}${date.getDate()}`;
    const month = `${date.getMonth() + 1 < 10 ? '0' : ''}${date.getMonth() + 1}`;
    const year = (date.getFullYear() + '').padStart(4, '0')
    const hour = (date.getHours() + '').padStart(2, '0')
    const min = (date.getMinutes() + '').padStart(2, '0')
    return `${year}-${month}-${day}T${hour}:${min}`;
}
